/**
 * ProductionEditorOverlaySidebar — port of the real Nemo Sidebar.
 *
 * Mirrors `apps/frontend-next/src/ui/views/layout/network/production-editor/flow/panels/sidebar/index.tsx`:
 *   - Renders inside ReactFlow as a `<Panel position="top-left">` overlay
 *     (NOT in a flex/grid sibling — overlay is a Nemo signature).
 *   - Two NJAccordion sections: "Plants" and "Components".
 *   - The Plants section lists selectable plants and a "Create a new plant"
 *     primary button beneath the list.
 *   - The Components section lists draggable components (palette).
 *
 * Use only inside `<ProductionEditorCanvas>` children prop:
 *   <ProductionEditorCanvas nodeTypes={NODE_TYPES}>
 *     <ProductionEditorOverlaySidebar plants={...} components={...} />
 *   </ProductionEditorCanvas>
 */
import { useState, type DragEvent, type SyntheticEvent } from 'react';
import {
    NJAccordion,
    NJAccordionItem,
    NJAccordionItemContent,
    NJAccordionItemHeader,
    NJButton,
    NJIcon,
} from '@engie-group/fluid-design-system-react';
import { Panel } from '@xyflow/react';
import { useProductionEditor } from './ProductionEditorProvider';
import { getNodeIcon } from './nodes/icons';

export interface PlantItem {
    id: string;
    label: string;
}

export interface ComponentItem {
    id: string;
    label: string;
    /** dragged node `entityName` (e.g. 'Boiler', 'VariableSpeedPump'). */
    entityName: string;
    /** Optional FDS icon name, used when no SVG is available. */
    icon?: string;
    /** Free-form metadata copied to the new node's data. */
    data?: Record<string, unknown>;
}

interface GroupedComponentsSection {
    title: string;
    items: ComponentItem[];
}

interface Props {
    plants: PlantItem[];
    components: ComponentItem[];
    selectedPlantId?: string | null;
    onSelectPlant?: (plantId: string) => void;
    onCreatePlant?: () => void;
    /** Show the Components accordion. Default true. */
    showComponents?: boolean;
    width?: number | string;
    /** Header label of the plants accordion. Default "Plants". */
    plantsLabel?: string;
    /** Header label of the components accordion. Default "Components". */
    componentsLabel?: string;
    /** Label of the create-plant button. Default "Create a new plant". */
    createPlantLabel?: string;
}

type NamedAccordionItem = HTMLDetailsElement & { name: string };

export function ProductionEditorOverlaySidebar({
    plants,
    components,
    selectedPlantId,
    onSelectPlant,
    onCreatePlant,
    showComponents = true,
    width = 240,
    plantsLabel = 'Plants',
    componentsLabel = 'Components',
    createPlantLabel = 'Create a new plant',
}: Props) {
    const { setDraggedItem } = useProductionEditor();
    const [, setIsOpen] = useState<Record<string, boolean>>({
        sites: true,
        components: false,
    });
    const handleToggle = (e: SyntheticEvent<NamedAccordionItem>) => {
        const { name } = e.currentTarget;
        setIsOpen((prev) => ({ ...prev, [name]: !prev[name] }));
    };

    const onDragStart = (event: DragEvent<HTMLDivElement>, item: ComponentItem) => {
        setDraggedItem({
            id: item.id,
            label: item.label,
            type: item.entityName,
            data: item.data,
        });
        event.dataTransfer.effectAllowed = 'move';
    };

    const hasGroupedTileLayout = components.some(
        (c) => typeof c.data?.componentGroup === 'string',
    );

    const groupedSections = components.reduce<GroupedComponentsSection[]>((acc, item) => {
        const title =
            (typeof item.data?.componentGroup === 'string' && item.data.componentGroup) ||
            'Autres';
        const existing = acc.find((section) => section.title === title);
        if (existing) {
            existing.items.push(item);
            return acc;
        }
        acc.push({ title, items: [item] });
        return acc;
    }, []);

    return (
        <Panel position="top-left" style={{ width }}>
            <NJAccordion
                style={{
                    border: '1px solid var(--nj-core-color-grey-300)',
                    background: 'white',
                }}
            >
                <NJAccordionItem name="sites" open onToggle={handleToggle}>
                    <NJAccordionItemHeader
                        style={{
                            padding: '0 10px',
                            minHeight: 48,
                        }}
                    >
                        {plantsLabel}
                    </NJAccordionItemHeader>
                    <NJAccordionItemContent style={{ padding: 0 }}>
                        <ul
                            style={{
                                listStyle: 'none',
                                padding: 0,
                                margin: 0,
                            }}
                        >
                            {plants.map((p) => {
                                const active = p.id === selectedPlantId;
                                return (
                                    <li key={p.id}>
                                        <button
                                            type="button"
                                            onClick={() => onSelectPlant?.(p.id)}
                                            style={{
                                                display: 'block',
                                                width: '100%',
                                                textAlign: 'left',
                                                padding: '12px 16px',
                                                background: active
                                                    ? 'var(--nj-semantic-color-background-brand-tertiary-default)'
                                                    : 'transparent',
                                                borderLeft: active
                                                    ? '3px solid var(--nj-semantic-color-border-brand-strong-default)'
                                                    : '3px solid transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontFamily: 'var(--nj-core-font-family-sans)',
                                                fontSize: 14,
                                                color: active
                                                    ? 'var(--nj-semantic-color-text-brand-primary-default)'
                                                    : 'var(--nj-semantic-color-text-neutral-primary-default)',
                                                fontWeight: active ? 600 : 400,
                                            }}
                                        >
                                            {p.label}
                                        </button>
                                    </li>
                                );
                            })}
                            {onCreatePlant && (
                                <li style={{ padding: 12 }}>
                                    <NJButton
                                        emphasis="bold"
                                        scale="md"
                                        label={createPlantLabel}
                                        onClick={onCreatePlant}
                                        style={{ width: '100%' }}
                                    />
                                </li>
                            )}
                        </ul>
                    </NJAccordionItemContent>
                </NJAccordionItem>
                {showComponents && (
                    <NJAccordionItem name="components" onToggle={handleToggle}>
                        <NJAccordionItemHeader
                            style={{
                                padding: '0 10px',
                                minHeight: 48,
                            }}
                        >
                            {componentsLabel}
                        </NJAccordionItemHeader>
                        <NJAccordionItemContent
                            style={{
                                padding: 0,
                                maxHeight: '60vh',
                                overflowY: 'auto',
                            }}
                        >
                            {hasGroupedTileLayout ? (
                                <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 14 }}>
                                    {groupedSections.map((section) => (
                                        <section key={section.title}>
                                            <div
                                                style={{
                                                    marginBottom: 8,
                                                    fontSize: 32,
                                                    lineHeight: 1.2,
                                                    color: 'var(--nj-semantic-color-text-neutral-secondary-default)',
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        fontSize: 13,
                                                        fontWeight: 600,
                                                        fontFamily: 'var(--nj-core-font-family-sans)',
                                                    }}
                                                >
                                                    {section.title}
                                                </span>
                                            </div>

                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                                                {section.items.map((c) => {
                                                    const iconSrc = getNodeIcon(c.entityName);
                                                    return (
                                                        <div
                                                            key={c.id}
                                                            draggable
                                                            onDragStart={(e) => onDragStart(e, c)}
                                                            title={`Glisser ${c.label} dans le schéma`}
                                                            aria-label={`Glisser ${c.label}`}
                                                            style={{
                                                                width: 60,
                                                                height: 60,
                                                                borderRadius: 10,
                                                                border: '1px solid var(--nj-core-color-grey-300)',
                                                                background: 'var(--nj-semantic-color-background-neutral-secondary-default)',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                cursor: 'grab',
                                                            }}
                                                        >
                                                            {iconSrc ? (
                                                                <img
                                                                    src={iconSrc}
                                                                    alt={c.label}
                                                                    width={32}
                                                                    height={32}
                                                                    style={{ display: 'block' }}
                                                                />
                                                            ) : c.icon ? (
                                                                <NJIcon name={c.icon} scale="sm" />
                                                            ) : null}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </section>
                                    ))}
                                </div>
                            ) : (
                                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                    {components.map((c) => (
                                        <li key={c.id}>
                                            <div
                                                draggable
                                                onDragStart={(e) => onDragStart(e, c)}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 12,
                                                    padding: '12px 16px',
                                                    cursor: 'grab',
                                                    borderTop: '1px solid var(--nj-core-color-grey-200)',
                                                }}
                                            >
                                                {(() => {
                                                    const iconSrc = getNodeIcon(c.entityName);
                                                    if (iconSrc) {
                                                        return (
                                                            <img
                                                                src={iconSrc}
                                                                alt={c.label}
                                                                width={20}
                                                                height={20}
                                                                style={{ display: 'block' }}
                                                            />
                                                        );
                                                    }
                                                    return c.icon ? <NJIcon name={c.icon} scale="sm" /> : null;
                                                })()}
                                                <span
                                                    style={{
                                                        fontSize: 13,
                                                        fontFamily: 'var(--nj-core-font-family-sans)',
                                                        color: 'var(--nj-semantic-color-text-neutral-primary-default)',
                                                    }}
                                                >
                                                    {c.label}
                                                </span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </NJAccordionItemContent>
                    </NJAccordionItem>
                )}
            </NJAccordion>
        </Panel>
    );
}
